import React, { useCallback, useEffect, useRef, useState } from "react";
import { ConfirmDialog, Modal, SearchInput } from "../../admin";

const API = process.env.REACT_APP_API_URL;
const STAT_FIELDS = [
  ["played", "Played"],
  ["won", "Won"],
  ["drawn", "Drawn"],
  ["lost", "Lost"],
  ["goalsFor", "Goals For"],
  ["goalsAgainst", "Goals Against"],
  ["points", "Points"],
];
const emptyForm = { clubId: "", leagueId: "", seasonYear: new Date().getFullYear(), played: 0, won: 0, drawn: 0, lost: 0, goalsFor: 0, goalsAgainst: 0, points: 0 };
const responseData = (body) => Array.isArray(body) ? body : body?.data || [];
// Years we probe when the API cannot list the seasons that hold standings.
const PROBE_YEARS = 11;
const seasonWindow = () => {
  const current = new Date().getFullYear();
  return Array.from({ length: PROBE_YEARS }, (_, index) => current + 1 - index);
};
const WRITE_UNAVAILABLE = "The API this dashboard is connected to does not have the league-table create/update/delete routes yet, so standings are read-only here. Deploy the updated backend to edit them.";

async function request(path, options) {
  const response = await fetch(`${API}${path}`, options);
  let body = null;
  try {
    body = await response.json();
  } catch (err) {
    body = null;
  }
  return { response, body: body || {} };
}

function LeagueTableEntryModal({ entry, leagues, clubs, onClose, onSave, saving }) {
  const [form, setForm] = useState(entry ? { ...entry } : { ...emptyForm });
  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  return (
    <Modal
      title={entry?.id ? "Edit standing" : "Add standing"}
      onClose={onClose}
      maxWidth="max-w-2xl"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" form="league-table-entry-form" className="btn-primary" disabled={saving}>
            {saving ? "Saving…" : entry?.id ? "Save changes" : "Create standing"}
          </button>
        </>
      }
    >
      <form id="league-table-entry-form" className="grid grid-cols-1 sm:grid-cols-2 gap-3" onSubmit={(event) => { event.preventDefault(); onSave(form); }}>
        <label className="field-label">League
          <select className="field mt-1" value={form.leagueId || ""} onChange={(event) => setField("leagueId", event.target.value)} required>
            <option value="">Choose a league</option>
            {leagues.map((league) => <option key={league.leagueId} value={league.leagueId}>{league.name}</option>)}
          </select>
        </label>
        <label className="field-label">Season
          <input className="field mt-1" type="number" min="1900" max="2200" value={form.seasonYear || ""} onChange={(event) => setField("seasonYear", event.target.value)} required />
        </label>
        <label className="field-label sm:col-span-2">Club
          <select className="field mt-1" value={form.clubId || ""} onChange={(event) => setField("clubId", event.target.value)} required>
            <option value="">Choose a club</option>
            {clubs.map((club) => <option key={club.clubId} value={club.clubId}>{club.name}</option>)}
          </select>
        </label>
        {STAT_FIELDS.map(([field, label]) => (
          <label className="field-label" key={field}>{label}
            <input className="field mt-1" type="number" min="0" step="1" value={form[field] ?? 0} onChange={(event) => setField(field, event.target.value)} required />
          </label>
        ))}
      </form>
    </Modal>
  );
}

export default function LeagueTablesTab() {
  const [leagues, setLeagues] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [seasonsError, setSeasonsError] = useState("");
  const [leagueId, setLeagueId] = useState("");
  const [seasonYear, setSeasonYear] = useState(new Date().getFullYear());
  const [entries, setEntries] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [readOnlyApi, setReadOnlyApi] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  // Which routes this API actually serves; the deployed build may predate them.
  const apiSupport = useRef({ seasons: null, entries: null });

  // Standings for one league/season. Prefers the CRUD route, falls back to the
  // long-standing /standings route so the tab also works against older builds.
  const loadEntriesFor = useCallback(async (nextLeagueId, nextSeasonYear) => {
    const year = Number(nextSeasonYear);
    if (!nextLeagueId || nextSeasonYear === "" || !Number.isInteger(year)) return [];

    if (apiSupport.current.entries !== false) {
      const { response, body } = await request(`/league-table/entries?leagueId=${encodeURIComponent(nextLeagueId)}&seasonYear=${encodeURIComponent(year)}`);
      if (response.ok) {
        apiSupport.current.entries = true;
        return responseData(body);
      }
      if (response.status !== 404) throw new Error(body.message || "Could not load standings");
      apiSupport.current.entries = false;
      setReadOnlyApi(true);
    }

    const { response, body } = await request(`/league-table/standings/${encodeURIComponent(nextLeagueId)}/${encodeURIComponent(year)}`);
    if (!response.ok) throw new Error(body.message || "Could not load standings");
    return responseData(body);
  }, []);

  // Seasons that hold standings. Older builds cannot list them, so we probe.
  const fetchSeasons = useCallback(async (forLeagueId) => {
    if (apiSupport.current.seasons !== false) {
      const { response, body } = await request("/league-table/seasons");
      if (response.ok) {
        apiSupport.current.seasons = true;
        const rows = responseData(body);
        setSeasons(rows);
        setSeasonsError("");
        return rows;
      }
      if (response.status !== 404) throw new Error(body.message || "Could not load league table seasons");
      apiSupport.current.seasons = false;
    }

    const probed = await Promise.all(
      seasonWindow().map(async (year) => {
        const { response, body } = await request(`/league-table/standings/${encodeURIComponent(forLeagueId)}/${year}`);
        return response.ok && responseData(body).length > 0 ? year : null;
      })
    );
    const rows = probed
      .filter((year) => year != null)
      .map((year) => ({ leagueId: Number(forLeagueId), seasonYear: year, leagueName: null, entries: null }));
    setSeasons(rows);
    setSeasonsError("");
    return rows;
  }, []);

  const loadEntries = useCallback(async () => {
    if (!leagueId) { setEntries([]); setLoading(false); return; }
    try {
      setLoading(true);
      setError("");
      setEntries(await loadEntriesFor(leagueId, seasonYear));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [leagueId, seasonYear, loadEntriesFor]);

  useEffect(() => {
    let active = true;
    const loadShell = async () => {
      try {
        const [leagueResponse, clubResponse] = await Promise.all([
          request("/leagues"),
          request("/clubs"),
        ]);
        if (!leagueResponse.response.ok) throw new Error("Could not load leagues");
        if (!clubResponse.response.ok) throw new Error("Could not load clubs");
        if (!active) return;

        const leagueRows = responseData(leagueResponse.body);
        setLeagues(leagueRows);
        setClubs(responseData(clubResponse.body));

        const firstLeagueId = leagueRows[0] ? String(leagueRows[0].leagueId) : "";
        setLeagueId(firstLeagueId);
        if (!firstLeagueId) { setLoading(false); return; }

        // Open on the newest season that actually holds standings.
        const found = await fetchSeasons(firstLeagueId);
        if (!active) return;
        const years = found.map((season) => Number(season.seasonYear)).filter((year) => Number.isInteger(year));
        if (years.length) setSeasonYear(Math.max(...years));
      } catch (err) {
        if (active) { setError(err.message); setLoading(false); }
      }
    };
    loadShell();
    return () => { active = false; };
  }, [fetchSeasons]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const saveEntry = async (form) => {
    try {
      setSaving(true);
      setError("");
      const isEdit = Boolean(editing?.id);
      const { response, body } = await request(
        `/league-table/entries${isEdit ? `/${editing.id}` : ""}`,
        {
          method: isEdit ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(Object.fromEntries(Object.entries(form).map(([key, value]) => [key, value === "" ? value : Number(value)]))),
        }
      );
      if (response.status === 404) throw new Error(WRITE_UNAVAILABLE);
      if (!response.ok) throw new Error(body.message || "Could not save standing");

      const nextLeagueId = String(form.leagueId);
      const nextSeasonYear = Number(form.seasonYear);
      setEditing(null);
      setLeagueId(nextLeagueId);
      setSeasonYear(nextSeasonYear);
      const [rows] = await Promise.all([loadEntriesFor(nextLeagueId, nextSeasonYear), fetchSeasons(nextLeagueId)]);
      setEntries(rows);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteEntry = async () => {
    if (!deleting) return;
    try {
      setSaving(true);
      setError("");
      const { response, body } = await request(`/league-table/entries/${deleting.id}`, { method: "DELETE" });
      if (response.status === 404) throw new Error(WRITE_UNAVAILABLE);
      if (!response.ok) throw new Error(body.message || "Could not delete standing");
      setDeleting(null);
      const [rows] = await Promise.all([loadEntriesFor(leagueId, seasonYear), fetchSeasons(leagueId)]);
      setEntries(rows);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const knownSeasons = Array.from(new Set(seasons.map((season) => Number(season.seasonYear))))
    .filter((year) => Number.isInteger(year) && year > 0)
    .sort((a, b) => b - a);

  const changeLeague = async (nextLeagueId) => {
    setLeagueId(nextLeagueId);
    if (!nextLeagueId) return;
    try {
      const found = await fetchSeasons(nextLeagueId);
      const years = found.map((season) => Number(season.seasonYear)).filter((year) => Number.isInteger(year));
      if (years.length) setSeasonYear(Math.max(...years));
    } catch (err) {
      setSeasonsError(err.message);
    }
  };

  const filtered = entries.filter((entry) => entry.club?.name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <select className="field sm:max-w-xs" aria-label="League" value={leagueId} onChange={(event) => changeLeague(event.target.value)}>
          <option value="">Choose league</option>
          {leagues.map((league) => <option key={league.leagueId} value={league.leagueId}>{league.name}</option>)}
        </select>
        <label className="field-label sm:max-w-[160px]">Season
          <input
            className="field mt-1"
            aria-label="Season"
            type="number"
            min="1900"
            max="2200"
            value={seasonYear}
            onChange={(event) => setSeasonYear(event.target.value === "" ? "" : Number(event.target.value))}
            onBlur={() => { if (seasonYear === "" || !Number.isInteger(seasonYear)) setSeasonYear(new Date().getFullYear()); }}
          />
        </label>
        {knownSeasons.length > 0 && knownSeasons.some((year) => year !== Number(seasonYear)) && (
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <span>Seasons with data</span>
            {knownSeasons.map((year) => (
              <button key={year} type="button" className="btn-ghost !py-1 !px-2" onClick={() => setSeasonYear(year)}>{year}</button>
            ))}
          </div>
        )}
        {seasonsError && (
          <p className="text-xs text-gray-500">Could not detect existing seasons ({seasonsError}); type the season you want.</p>
        )}
        <SearchInput value={search} onChange={setSearch} placeholder="Search clubs…" />
        <button type="button" className="btn-primary shrink-0" onClick={() => setEditing({ ...emptyForm, leagueId, seasonYear })} disabled={!leagueId}>Add standing</button>
      </div>

      {readOnlyApi && (
        <div className="panel px-4 py-3 text-sm text-gray-300 border-line-strong">
          <strong className="text-white">Read-only:</strong> this API build exposes the standings route but not the create/update/delete routes, so editing and deleting need the updated backend deployed.
        </div>
      )}
      {error && <div role="alert" className="panel px-4 py-3 text-sm text-accent-soft border-accent-dim">{error}</div>}
      <div className="card overflow-hidden">
        {loading ? <div className="py-14 text-center text-gray-400">Loading standings…</div> : filtered.length === 0 ? (
          <div className="py-12 text-center"><p className="font-semibold text-white">No standings found</p><p className="mt-1 text-sm text-gray-500">Choose another season or add the first club standing.</p></div>
        ) : (
          <div className="overflow-x-auto"><table className="min-w-full text-sm">
            <thead><tr className="table-head"><th className="px-3.5 py-2.5 text-left">Pos</th><th className="px-3.5 py-2.5 text-left">Club</th>{STAT_FIELDS.map(([, label]) => <th key={label} className="px-3.5 py-2.5 text-right">{label}</th>)}<th className="px-3.5 py-2.5 text-right sticky right-0 bg-surface-600 border-l border-line-strong">Actions</th></tr></thead>
            <tbody>{filtered.map((entry, index) => <tr key={entry.id} className="table-row group">
              <td className="px-3.5 py-2.5 tabular-nums">{entry.position || index + 1}</td><td className="px-3.5 py-2.5 text-white font-medium">{entry.club?.name || `Club ${entry.clubId}`}</td>
              {STAT_FIELDS.map(([field]) => <td key={field} className="px-3.5 py-2.5 text-right tabular-nums">{entry[field] ?? 0}</td>)}
              <td className="px-3.5 py-2 sticky right-0 bg-surface-850 group-hover:bg-surface-700 border-b border-l border-line"><div className="flex justify-end gap-2"><button type="button" className="btn-ghost !py-1.5 !px-3" onClick={() => setEditing(entry)}>Edit</button><button type="button" className="btn-danger !py-1.5 !px-3" onClick={() => setDeleting(entry)}>Delete</button></div></td>
            </tr>)}</tbody>
          </table></div>
        )}
      </div>

      {editing && <LeagueTableEntryModal entry={editing} leagues={leagues} clubs={clubs} onClose={() => setEditing(null)} onSave={saveEntry} saving={saving} />}
      {deleting && <ConfirmDialog title="Delete standing" message={`Remove ${deleting.club?.name || "this club"} from the ${seasonYear} league table?`} confirmLabel="Delete standing" onConfirm={deleteEntry} onCancel={() => setDeleting(null)} busy={saving} />}
    </section>
  );
}
